import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import EditRecord from './EditRecord';

function RecordList({ userId, onClose, onUpdate, guestMode }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [recordToEdit, setRecordToEdit] = useState(null);

  // 날짜 형식 변환 (2025-10-24 → 25.10.24)
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${year.slice(2)}.${month}.${day}`;
  };

  useEffect(() => {
    loadRecords();
  }, [userId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      if (guestMode) {
        // 비회원 모드: localStorage에서 로드
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        guestRecords.sort((a, b) => new Date(b.date) - new Date(a.date));
        setRecords(guestRecords);
      } else {
        // 회원 모드: Firebase에서 로드
        const recordsRef = collection(db, 'users', userId, 'records');
        const q = query(recordsRef, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);

        const recordsData = [];
        querySnapshot.forEach((doc) => {
          recordsData.push({
            id: doc.id,
            ...doc.data()
          });
        });

        setRecords(recordsData);
      }
    } catch (error) {
      console.error('기록 로드 실패:', error);
      alert('기록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setDeleteConfirmId(record.id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      if (guestMode) {
        // 비회원 모드: localStorage에서 삭제
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        const updatedRecords = guestRecords.filter(r => r.id !== deleteConfirmId);
        localStorage.setItem('guestRecords', JSON.stringify(updatedRecords));
      } else {
        // 회원 모드: Firebase에서 삭제
        await deleteDoc(doc(db, 'users', userId, 'records', deleteConfirmId));
      }
      await loadRecords();
      onUpdate();
      setDeleteConfirmId(null);
      setRecordToDelete(null);
    } catch (error) {
      console.error('기록 삭제 실패:', error);
      alert('기록 삭제에 실패했습니다.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
    setRecordToDelete(null);
  };

  const handleEditClick = (record) => {
    setRecordToEdit(record);
  };

  const handleEditSuccess = async () => {
    await loadRecords();
    onUpdate();
    setRecordToEdit(null);
  };

  const handleExportToCSV = () => {
    if (records.length === 0) {
      alert('내보낼 기록이 없습니다.');
      return;
    }

    // CSV 헤더
    let csvContent = '\uFEFF'; // UTF-8 BOM for Excel
    csvContent += '날짜,키(cm),몸무게(kg),메모\n';

    // CSV 데이터
    records.forEach(record => {
      const memo = record.memo ? record.memo.replace(/"/g, '""') : '';
      csvContent += `${record.date},${record.height},${record.weight},"${memo}"\n`;
    });

    // Blob 생성 및 다운로드
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `성장기록_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    alert('엑셀 파일이 다운로드되었습니다!');
  };

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355'
          }}
        >
          ×
        </button>

        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
          <h2 style={{
            color: '#E8A87C',
            fontSize: '24px',
            marginBottom: '8px'
          }}>
            전체 기록
          </h2>
          <p style={{
            color: '#8B7355',
            fontSize: '14px'
          }}>
            {records.length}개의 성장 기록
          </p>
        </div>

        {loading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                backgroundColor: '#FAF3E8',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '12px'
              }}>
                <div className="skeleton skeleton-title" style={{ width: '40%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '60%' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '30%' }}></div>
              </div>
            ))}
          </div>
        ) : records.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#8B7355'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <p>아직 기록이 없습니다.</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              "기록 추가" 버튼을 눌러 첫 기록을 남겨보세요!
            </p>
          </div>
        ) : (
          <div>
            {records.map((record) => (
              <div
                key={record.id}
                style={{
                  backgroundColor: '#FAF3E8',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: '#E8A87C',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    marginBottom: '8px'
                  }}>
                    {formatDate(record.date)}
                  </div>
                  <div style={{
                    color: '#8B7355',
                    fontSize: '14px',
                    display: 'flex',
                    gap: '16px',
                    marginBottom: record.memo ? '8px' : '0'
                  }}>
                    <span>키: {record.height}cm</span>
                    <span>몸무게: {record.weight}kg</span>
                  </div>
                  {record.memo && (
                    <div style={{
                      color: '#8B7355',
                      fontSize: '12px',
                      fontStyle: 'italic',
                      backgroundColor: '#FFF8F0',
                      padding: '6px 10px',
                      borderRadius: '8px',
                      marginTop: '8px'
                    }}>
                      📝 {record.memo}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEditClick(record)}
                    style={{
                      backgroundColor: 'white',
                      border: '2px solid #E8A87C',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#E8A87C',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#FAF3E8';
                      e.target.style.borderColor = '#D4956B';
                      e.target.style.color = '#D4956B';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#E8A87C';
                      e.target.style.color = '#E8A87C';
                    }}
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteClick(record)}
                    style={{
                      backgroundColor: 'white',
                      border: '2px solid #F5E6D3',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#D4956B',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#FEE';
                      e.target.style.borderColor = '#FCC';
                      e.target.style.color = '#C44';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#F5E6D3';
                      e.target.style.color = '#D4956B';
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {records.length > 0 && (
          <button
            onClick={handleExportToCSV}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#66BB6A',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginTop: '20px',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4CAF50'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#66BB6A'}
          >
            📊 엑셀로 내보내기
          </button>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '12px',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>

      {/* 삭제 확인 다이얼로그 */}
      {deleteConfirmId && recordToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100
        }}>
          <div className="slide-up" style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '340px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{
              color: '#E8A87C',
              fontSize: '20px',
              marginBottom: '12px'
            }}>
              기록 삭제
            </h3>
            <p style={{
              color: '#8B7355',
              fontSize: '14px',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              <strong>{formatDate(recordToDelete.date)}</strong> 기록을<br />
              정말 삭제하시겠습니까?<br />
              <span style={{ fontSize: '12px', color: '#D4956B' }}>
                (키: {recordToDelete.height}cm, 몸무게: {recordToDelete.weight}kg)
              </span>
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '14px',
                  backgroundColor: 'white',
                  border: '2px solid #F5E6D3',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: '#8B7355',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: '14px',
                  backgroundColor: '#E57373',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 기록 수정 모달 */}
      {recordToEdit && (
        <EditRecord
          userId={userId}
          record={recordToEdit}
          onClose={() => setRecordToEdit(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}

export default RecordList;
